/* eslint-disable react/react-compiler -- false positive: prop forwarding, not ref.current reads */
import {flip, offset, shift, useFloating, useMergeRefs} from '@floating-ui/react';
import {Show, Text} from 'platform/foundation';
import styled, {useTheme} from 'styled-components';

import {FocusEvent, ReactNode, useMemo, useRef, useState} from 'react';

import {isNil} from 'ramda';

import {
  suffixTestId,
  TestIdProps,
  useBoolean,
  containsFocusableElements,
  useOnMount,
} from 'shared/web';

import {Placement} from '../../types/Placement';
import {Portal} from '../Portal/components/Portal';
import {PortalManager} from '../Portal/components/PortalManager';

type TooltipPlacement = Exclude<Placement, 'auto'>;

const OFFSET = 10;

export interface TooltipProps extends TestIdProps {
  children: ReactNode;
  label?: ReactNode | string;
  description?: ReactNode;
  placement?: TooltipPlacement;
  overflowWrap?: 'anywhere';
  childrenWrapperDisplay?: 'flex' | 'block';
  isOpen?: boolean;
  isDisabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
  /**
   * Keeps the tooltip open while the pointer is over its content,
   * so interactive elements (links) inside the tooltip can be clicked.
   * Applies a default closeDelay of 150 ms unless closeDelay is set.
   */
  isInteractive?: boolean;
  hasAutoWidth?: boolean;
  offset?: number;
  shouldWrapperStretch?: boolean;
  onOpen?(): void;
  onClose?(): void;
}

export function Tooltip(props: TooltipProps) {
  const theme = useTheme();

  const [isOpen, setOpen, setClose] = useBoolean(props.isOpen);
  const [hasFocusableChildren, setHasFocusableChildren] = useState(false);
  const [hasExplicitlyNonFocusableChildren, setHasExplicitlyNonFocusableChildren] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {refs, floatingStyles} = useFloating({
    placement: props.placement,
    middleware: [offset(props.offset ?? OFFSET), shift(), flip()],
    strategy: props.hasAutoWidth ? 'fixed' : 'absolute',
  });

  // useMemo is required for correctness: useMergeRefs depends on array identity.
  // A new [] each render yields a new ref callback; React 19 cleanup then calls
  // setDomReference in a loop (Maximum update depth exceeded / TEAS-FE-7WG).
  const referenceRefs = useMemo(() => [refs.setReference, wrapperRef], [refs.setReference]);
  const mergedReferenceRef = useMergeRefs(referenceRefs);

  // Focusability is only checked on mount. Tooltip children are expected to be
  // structurally static; re-checking on every render caused a render loop because
  // React always produces new element references.
  useOnMount(() => {
    setHasFocusableChildren(containsFocusableElements(wrapperRef.current));

    const hasNonFocusableElements =
      wrapperRef.current?.querySelectorAll('[tabindex="-1"]').length ?? 0;
    setHasExplicitlyNonFocusableChildren(hasNonFocusableElements > 0);
  });

  useOnMount(function registerTooltipTimerCleanup() {
    return function clearTooltipTimerOnUnmount() {
      if (isNil(timerRef.current)) {
        return;
      }

      clearTimeout(timerRef.current);
    };
  });

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    const isKeyboardNavigation = event.target?.matches(':focus-visible');

    if (isKeyboardNavigation) {
      handleOpen();
    }
  };

  const effectiveCloseDelay = props.closeDelay ?? (props.isInteractive ? 150 : undefined);

  const handleOpen = () => {
    const shouldOpen = props.isOpen ?? true;

    if (props.isDisabled || !shouldOpen) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isNil(props.openDelay)) {
      setOpen();
      props.onOpen?.();
      return;
    }

    timerRef.current = setTimeout(() => {
      setOpen();
      props.onOpen?.();
    }, props.openDelay);
  };

  const handleClose = () => {
    const shouldClose = props.isOpen !== true;

    if (!shouldClose) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isNil(effectiveCloseDelay)) {
      setClose();
      props.onClose?.();
      return;
    }

    timerRef.current = setTimeout(() => {
      setClose();
      props.onClose?.();
    }, effectiveCloseDelay);
  };

  if (isNil(props.label) && isNil(props.description)) {
    return <>{props.children}</>;
  }

  return (
    <>
      <StyledChildrenWrapper
        $display={props.childrenWrapperDisplay}
        $shouldStretch={props.shouldWrapperStretch}
        ref={mergedReferenceRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleFocus}
        onBlur={handleClose}
        tabIndex={
          hasFocusableChildren || hasExplicitlyNonFocusableChildren || props.isDisabled
            ? undefined
            : 0
        }
      >
        {props.children}
      </StyledChildrenWrapper>
      <Show when={isOpen}>
        <PortalManager zIndex={theme.zIndices.TOOLTIP}>
          <Portal>
            <span
              ref={refs.setFloating}
              style={floatingStyles}
              {...(props.isInteractive
                ? {onMouseEnter: handleOpen, onMouseLeave: handleClose}
                : {})}
            >
              <StyledContentWrapper $hasAutoWidth={props.hasAutoWidth}>
                <Show when={props.label}>
                  <Text size="xSmall" alternative color="white" overflowWrap={props.overflowWrap}>
                    {props.label}
                  </Text>
                </Show>
                <Show when={props.description}>
                  <Text
                    size="xSmall"
                    color="white"
                    data-testid={suffixTestId(`tooltipDescription`, props)}
                    overflowWrap={props.overflowWrap}
                  >
                    {props.description}
                  </Text>
                </Show>
              </StyledContentWrapper>
            </span>
          </Portal>
        </PortalManager>
      </Show>
    </>
  );
}

type StyledChildrenWrapperProps = {
  $display?: TooltipProps['childrenWrapperDisplay'];
  $shouldStretch?: boolean;
};

const StyledChildrenWrapper = styled.div<StyledChildrenWrapperProps>`
  display: ${(props) => props.$display ?? 'block'};
  width: ${(props) => (props.$shouldStretch ? '100%' : 'auto')};
`;

type StyledContentWrapperProps = {
  $hasAutoWidth?: TooltipProps['hasAutoWidth'];
};

const StyledContentWrapper = styled.div<StyledContentWrapperProps>`
  background-color: ${(props) => props.theme.colors.palettes.neutral[900][100]};
  border-radius: ${(props) => props.theme.radii.small};
  max-width: ${(props) => (props.$hasAutoWidth ? '50vw' : props.theme.getSize(58))};
  padding: ${(props) => props.theme.getSize(1)} ${(props) => props.theme.getSize(2)};
`;
